import React, { useCallback, useImperativeHandle, useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardNumberElement, CardExpiryElement, CardCvcElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { setupStripeIntent } from '@/service/billing';
import { countries } from 'countries-list';
import { toast } from 'sonner';

const stripePromise = loadStripe(process.env.STRIPE_PUBLIC_KEY ?? '');

const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      color: '#32325d',
      fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
      fontSmoothing: 'antialiased',
      fontSize: '16px',
      height: '48px',
      '::placeholder': {
        color: '#71717a',
      },
    },
    invalid: {
      color: '#bc3229',
      iconColor: '#bc3229',
    },
  },
  showIcon: true,
  disableLink: true,
};

const PaymentForm = ({ formRef }: { formRef?: React.RefObject<any> }) => {
  const stripe = useStripe();
  const elements = useElements();

  const [formData, setFormData] = useState({
    name: '',
    country: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    postalCode: '',
    state: '',
    isBusiness: false,
    sameBillingAddress: false,
    businessName: '',
    businessCountry: '',
    businessAddressLine1: '',
    businessAddressLine2: '',
    businessCity: '',
    businessPostalCode: '',
    businessState: '',
    taxIdType: '',
    taxId: '',
  });

  const handleChange = (e: any) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const handleSelectChange = (name: any, value: any) => {
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleCheckboxChange = (name: any, checked: any) => {
    setFormData({
      ...formData,
      [name]: checked,
    });
  };
  const handleSubmit = useCallback(
    async (event?: any) => {
      if (event?.preventDefault) {
        event.preventDefault();
      }
      if (!stripe || !elements) {
        return;
      }

      const cardNumberElement = elements.getElement(CardNumberElement);
      //   const cardExpiryElement = elements.getElement(CardExpiryElement);
      //   const cardCvcElement = elements.getElement(CardCvcElement);
      if (!cardNumberElement) return;

      try {
        if (!formData.country) {
          throw new Error('please select a country');
        }
        const response = (await setupStripeIntent()) as {
          data: {
            client_secret: string;
            payment_id: string;
          };
        };
        // todo: payment_id use to check bind card status, use /api/v1/payment/payment_status/:payment_id
        const { client_secret: clientSecret, payment_id } = response.data;

        const { error: stripeError, setupIntent } = await stripe.confirmCardSetup(clientSecret, {
          payment_method: {
            card: cardNumberElement,
            billing_details: {
              address: {
                city: formData.city,
                country: formData.country,
                line1: formData.businessAddressLine1,
                line2: formData.businessAddressLine2,
                postal_code: formData.postalCode,
                state: formData.state,
              },
              email: null,
              name: formData.name,
              phone: null,
            },
          },
        });

        if (stripeError) {
          throw new Error(stripeError.message);
        }
      } catch (error: any) {
        toast.error(error?.message || 'Error.');
        throw error;
      }
    },
    [stripe, elements, formData],
  );
  useImperativeHandle(
    formRef,
    () => ({
      handleSubmit,
    }),
    [formRef, handleSubmit],
  );

  return (
    // <form onSubmit={handleSubmit}>
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="card-number">Card Number</Label>
        <div className="border rounded-md p-3">
          <CardNumberElement id="card-number" options={CARD_ELEMENT_OPTIONS} />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="card-expiry">Expiration Date</Label>
          <div className="border rounded-md p-3">
            <CardExpiryElement id="card-expiry" options={CARD_ELEMENT_OPTIONS} />
          </div>
        </div>
        <div className="space-y-2">
          <Label htmlFor="card-cvc">CVC</Label>
          <div className="border rounded-md p-3">
            <CardCvcElement id="card-cvc" options={CARD_ELEMENT_OPTIONS} />
          </div>
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="name">Name On Card</Label>
        <Input
          className="h-[48px]"
          id="name"
          name="name"
          value={formData.name}
          onChange={handleChange}
          placeholder="Name On Card"
          required
        />
      </div>

      <div className="space-y-2">
        <h3 className="text-sm font-medium">Billing Address</h3>
        {/* <AddressElement options={{ mode: 'billing' }} /> */}
        <Select value={formData.country} onValueChange={(value) => handleSelectChange('country', value)} required>
          <SelectTrigger className="w-full h-[48px]">
            <SelectValue className="" placeholder="Country" />
          </SelectTrigger>
          <SelectContent className="overflow-y-auto h-[400px]">
            <SelectGroup>
              {Object.entries(countries)
                .sort(([val1, country1], [val2, country2]) => {
                  return country1.name.localeCompare(country2.name);
                })
                .map(([val, country]) => {
                  return (
                    <SelectItem key={val} value={val}>
                      {country.name}
                    </SelectItem>
                  );
                })}
            </SelectGroup>
          </SelectContent>
        </Select>

        <Input
          className="h-[48px]"
          name="addressLine1"
          value={formData.addressLine1}
          onChange={handleChange}
          placeholder="Address line 1"
          required
        />

        <Input
          className="h-[48px]"
          name="addressLine2"
          value={formData.addressLine2}
          onChange={handleChange}
          placeholder="Address line 2"
        />

        <div className="grid grid-cols-2 gap-4">
          <Input className="h-[48px]" name="city" value={formData.city} onChange={handleChange} placeholder="City" required />
          <Input
            className="h-[48px]"
            name="postalCode"
            value={formData.postalCode}
            onChange={handleChange}
            placeholder="Postal code"
            required
          />
        </div>

        <Input
          className="h-[48px]"
          name="state"
          value={formData.state}
          onChange={handleChange}
          placeholder="State, country, province or region"
        />
      </div>

      {/* <div className="flex items-center space-x-2">
        <Checkbox
          id="isBusiness"
          checked={formData.isBusiness}
          onCheckedChange={(checked) => handleCheckboxChange('isBusiness', checked)}
        />
        <Label htmlFor="isBusiness" className=" text-jen-content-secondary cursor-pointer">
          Purchasing as a business
        </Label>
      </div> */}

      {formData.isBusiness && (
        <>
          <div className="space-y-2">
            <h3 className="text-sm font-medium">Primary business address</h3>
            <p className="text-sm text-muted-foreground text-jen-content-secondary">
              This is the primary address for your business and what Jen will use to calculate any applicable tax.
            </p>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="sameBillingAddress"
                checked={formData.sameBillingAddress}
                onCheckedChange={(checked) => handleCheckboxChange('sameBillingAddress', checked)}
              />
              <Label htmlFor="sameBillingAddress" className=" text-jen-content-secondary cursor-pointer">
                Same as Billing Address
              </Label>
            </div>
            <div className="space-y-2">
              <Label htmlFor="businessName">Business Name</Label>
              <Input
                className="h-[48px]"
                id="businessName"
                name="businessName"
                value={formData.businessName}
                onChange={handleChange}
                placeholder="Business Name"
                required={formData.isBusiness}
              />
            </div>
            {!formData.sameBillingAddress && (
              <>
                <h3 className="text-sm font-medium">Billing Address</h3>

                <Select value={formData.businessCountry} onValueChange={(value) => handleSelectChange('businessCountry', value)}>
                  <SelectTrigger className="h-[48px]">
                    <SelectValue placeholder="Country" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      {Object.entries(countries).map(([val, country]) => {
                        return <SelectItem value={val}>{country.name}</SelectItem>;
                      })}
                    </SelectGroup>

                    {/* Add more countries */}
                  </SelectContent>
                </Select>

                <Input
                  className="h-[48px]"
                  name="businessAddressLine1"
                  value={formData.businessAddressLine1}
                  onChange={handleChange}
                  placeholder="Address line 1"
                  required={formData.isBusiness}
                />

                <Input
                  className="h-[48px]"
                  name="businessAddressLine2"
                  value={formData.businessAddressLine2}
                  onChange={handleChange}
                  placeholder="Address line 2"
                />

                <div className="grid grid-cols-2 gap-4">
                  <Input
                    className="h-[48px]"
                    name="businessCity"
                    value={formData.businessCity}
                    onChange={handleChange}
                    placeholder="City"
                    required={formData.isBusiness}
                  />
                  <Input
                    className="h-[48px]"
                    name="businessPostalCode"
                    value={formData.businessPostalCode}
                    onChange={handleChange}
                    placeholder="Postal code"
                    required={formData.isBusiness}
                  />
                </div>

                <Input
                  className="h-[48px]"
                  name="businessState"
                  value={formData.businessState}
                  onChange={handleChange}
                  placeholder="State, country, province or region"
                />
              </>
            )}

            <div className="space-y-2">
              <h3 className="text-sm font-medium">Business tax ID</h3>
              <p className="text-sm text-muted-foreground">Please enter your tax ID here</p>

              <div className="grid grid-cols-2 gap-4">
                <Select value={formData.taxIdType} onValueChange={(value) => handleSelectChange('taxIdType', value)}>
                  <SelectTrigger className="h-[48px]">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      <SelectItem value="ein">EIN (US)</SelectItem>
                      <SelectItem value="vat">VAT ID (EU)</SelectItem>
                      <SelectItem value="abn">ABN (Australia)</SelectItem>
                    </SelectGroup>

                    {/* Add more tax ID types */}
                  </SelectContent>
                </Select>

                <Input
                  className="h-[48px]"
                  name="taxId"
                  value={formData.taxId}
                  onChange={handleChange}
                  placeholder="Enter tax ID"
                  required={formData.isBusiness}
                />
              </div>
            </div>
          </div>
        </>
      )}

      {/* <Button type="submit" disabled={!stripe} className="w-full">
        支付
      </Button> */}
    </div>
    // </form>
  );
};

// 包装组件以提供 Stripe 上下文
const CardForm = ({ formRef }: { formRef?: React.RefObject<{ handleSubmit: () => void }> }) => {
  return (
    <Elements stripe={stripePromise}>
      <PaymentForm formRef={formRef} />
    </Elements>
  );
};

export default CardForm;
